import * as React from 'react';

import config from 'js/app/config';
import path from 'js/lib/path';

import { prependUrlProtocolIfNone } from 'bundles/common/utils/urlUtils';
import { websitesInfo } from 'bundles/partners-graphql/constants';
import type { Instructor, InstructorBreadcrumbLink } from 'bundles/partners-graphql/types/Instructor';
import GoogleMarkup from 'bundles/seo/components/GoogleMarkup';
import { getSchemaBreadcrumbsList } from 'bundles/seo/utils/schemaMarkupUtils';
import { clearEmptyFields } from 'bundles/seo/utils/utils';

type Props = {
  instructor: Instructor;
  description: string;
  pathname: string;
  breadcrumbLinks: Array<InstructorBreadcrumbLink>;
};

const WEBSITE_KEYS = ['website', 'websiteFacebook', 'websiteTwitter', 'websiteLinkedin'] as const;

const InstructorProfileGoogleSchemaMarkup = ({ instructor, description, pathname, breadcrumbLinks }: Props) => {
  const constructInstructorExternalLinks = () => {
    const { websites } = instructor;
    const instructorExternalLinks: Record<string, string> = {};

    WEBSITE_KEYS.forEach((key) => {
      const externalLinkInfo = websites?.[key] && websitesInfo[key];
      if (externalLinkInfo) {
        instructorExternalLinks[key] = prependUrlProtocolIfNone(`${externalLinkInfo.prefix}${websites?.[key]}`) || '';
      }
    });

    return instructorExternalLinks;
  };

  const pageUrl = path.join(config.url.base, pathname);

  const markupJson = {
    '@type': 'WebPage',
    Name: `${instructor.fullName}, Instructor | Coursera`,
    url: pageUrl,
    about: {
      '@id': `${pageUrl}#instructor`,
      '@type': 'Person',
      name: `${instructor.fullName}`,
      description,
      image: `${instructor.photo}`,
      url: instructor.partners?.elements.map((partner: $TSFixMe) => partner.links.website),
      sameAs: Object.values(constructInstructorExternalLinks()),
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: instructor.courses?.elements.map((course: $TSFixMe, i: $TSFixMe) => ({
        '@type': 'ListItem',
        position: i + 1,
        '@id': `${path.join(config.url.base, 'learn', course.slug)}#mooc`,
        url: path.join(config.url.base, 'learn', course.slug),
      })),
    },
  };

  const breadcrumbList = getSchemaBreadcrumbsList(
    breadcrumbLinks.map(({ label, href }) => ({
      name: label,
      absoluteUrl: href ? path.join(config.url.base, href) : undefined,
    }))
  );

  return (
    <div className="rc-InstructorProfileGoogleSchemaMarkup">
      <GoogleMarkup
        graph={{ '@context': 'https://schema.org', '@graph': [clearEmptyFields(markupJson), breadcrumbList] }}
      />
    </div>
  );
};

export default InstructorProfileGoogleSchemaMarkup;
